import { MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react'
import { asTimeDuration, displayDate } from 'lib/util/date'
import {
  ClipboardListIcon,
  LinkIcon,
  MessageSquareIcon,
  PencilIcon,
  QuoteIcon,
  SendIcon,
  SparklesIcon,
  XIcon,
} from 'lucide-react'

import Checkbox from 'lib/components/checkbox/checkbox'
import { AdminTaskType, AdminTimelineItem } from 'lib/api/admin/timeline/admin-timeline'
import { IconFlyoutMenu } from 'lib/components/dropdown-icon-menu/icon-flyout-menu'
import IconButton from 'lib/components/buttons/icon-button'
import WYSIWYGTextarea from 'lib/components/wysiwyg/wysiwyg-textarea'
import { useAdminTimelineContext } from '../providers/admin-timeline-provider'
import { capitalize } from 'lib/string/utils'
import { AdminTicketState } from 'lib/api/admin/tickets/admin-tickets'

import Avatar from 'components/core/avatar'
import { useAdminTicketContext } from '../providers/admin-ticket-provider'
import { useAdminAnnotationHighlightContext } from '../providers/admin-annotation-highlight-provider'
import { Annotation } from 'lib/api/annotations/annotations'
import { VideoJSAnnotation } from 'lib/api/ticket-files/ticket-files'
import { useAdminMediaContext } from '../providers/admin-media-provider'
import hyperlink from 'lib/util/hyperlink'
import { toast } from 'lib/components/toast/toast'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'
import parseDirectionHTML from 'lib/util/parse-direction-html'
import { parseAssetUrls } from 'lib/util/parse-asset-urls'
import { useS3PreviewImage } from 'lib/hooks/useS3PreviewImage'
import { getPreviewUrl } from 'lib/util/file/file'

const classNames = {
  article:
    'timeline-item tw-ml-2 tw-mb-4 tw-text-neutral-800 tw-rounded-lg tw-p-4 tw-max-w-xl tw-transition-colors tw-ease-in-out tw-duration-300 tw-break-words',
  body: 'tw-py-4 tw-whitespace-pre-wrap',
  error: 'tw-border tw-border-solid tw-border-gray-300 tw-rounded tw-p-4 tw-mb-4 tw-text-center',
  form: {
    container: 'tw-flex tw-items-center tw-gap-2 tw-pb-4',
    textarea: 'tw-w-full tw-border tw-border-solid tw-border-gray-300 tw-rounded tw-p-2',
  },
  header: {
    avatar: 'tw-shrink',
    byline: 'tw-flex-grow tw-text-left tw-flex',
    container: 'tw-flex tw-direction-row tw-justify-between tw-items-center tw-p-2 tw-gap-2',
  },
  li: 'tw-flex tw-items-center',
}

function TypeTitle({ type }: { type: string }): ReactElement {
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()
  switch (type) {
    case AdminTaskType.GenAiRequest:
      return (
        <>
          <SparklesIcon className="lu-light lu-md" /> AI REQUEST
        </>
      )
    case AdminTaskType.Conversation:
      return (
        <>
          <MessageSquareIcon className="lu-light lu-md" /> MESSAGE
        </>
      )
    default:
      if (!isFeatureFlagEnabled('request_wysiwyg')) {
        switch (type) {
          case AdminTaskType.Direction:
            return (
              <>
                <ClipboardListIcon className="lu-light lu-md" /> GENERAL DIRECTION
              </>
            )
          case AdminTaskType.Copy:
            return (
              <>
                <QuoteIcon className="lu-light lu-md" /> EXACT COPY
              </>
            )
          default:
            return null
        }
      }
      return null
  }
}

interface TimelineItemProps {
  item: AdminTimelineItem
}

const getTimelineItemDisplayType = (item: AdminTimelineItem): string => {
  if (item.taskType === AdminTaskType.Annotation) {
    return 'annotation'
  }
  if (item.taskType === AdminTaskType.Conversation && item.private) {
    return 'note'
  }

  return 'message'
}

const isAnnotation = (item: AdminTimelineItem): boolean => {
  return item.taskType === AdminTaskType.Annotation
}

const isVideoAnnotation = (annotation: Annotation): boolean => {
  return !!(annotation?.data as VideoJSAnnotation)?.range
}

export default function AdminTimelineDisplayItem({ item }: TimelineItemProps): ReactElement {
  const [isLoadingCheck, setIsLoadingCheck] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedBodyValue, setEditedBodyValue] = useState(item.body)
  const [parsedBody, setParsedBody] = useState(item.body)
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()

  const { ticket } = useAdminTicketContext()
  const { updateTimelineItem, toggleCompletedStatus } = useAdminTimelineContext()
  const { clickedAnnotation, hoveredAnnotation, setHoveredAnnotation, setClickedAnnotation } =
    useAdminAnnotationHighlightContext()
  const { getFilePageNumber, getParentFileName } = useAdminMediaContext()
  const displayItemType = getTimelineItemDisplayType(item)
  const checkDisabled = ticket.state !== AdminTicketState.inProgress || isLoadingCheck
  const isSubmittedByCustomer =
    (item.taskType === AdminTaskType.Conversation && item.incoming) ||
    (item.taskType !== AdminTaskType.Conversation && !item.private)
  const containerId = item?.annotation?.id ? `timeline-annotation-${item?.annotation?.id}` : `timeline-item-${item.id}`
  const isNoteOrPrivateTask =
    item.private &&
    (item.taskType === AdminTaskType.Copy ||
      item.taskType === AdminTaskType.Direction ||
      item.taskType == AdminTaskType.Conversation)
  const canEdit = item.ticketVersion === ticket.currentVersion && isNoteOrPrivateTask

  const fileName = useMemo(() => {
    if (item?.annotation?.fileParentId) {
      return getParentFileName(item.annotation.fileParentId) || item?.annotation?.fileName
    }

    return item?.annotation?.fileName
  }, [getParentFileName, item?.annotation?.fileName, item?.annotation?.fileParentId])

  const fileCaption = useMemo(() => {
    if (isVideoAnnotation(item?.annotation)) {
      return asTimeDuration((item.annotation.data as VideoJSAnnotation).range.start)
    }

    if (item?.annotation?.fileParentId) {
      const pageNumber = getFilePageNumber(item.annotation.fileParentId, item.annotation.assetId)
      return `pg.${pageNumber}`
    }

    return null
  }, [getFilePageNumber, item?.annotation])

  async function handleCheckboxClick() {
    try {
      setIsLoadingCheck(true)
      await toggleCompletedStatus(item)
      setIsLoadingCheck(false)
      // Item has not been updated yet so we are doing the opposite here
      toast.success(`${capitalize(displayItemType)} has been marked as ${!item.completed ? 'complete' : 'incomplete'}`)
    } catch (error) {
      console.error(`Error marking ${displayItemType} as ${!item.completed ? 'complete' : 'incomplete'}`, error)
      toast.error(`Error marking ${displayItemType} as ${!item.completed ? 'complete' : 'incomplete'}`)
    }
  }

  function itemColorClass() {
    if (
      item.annotation &&
      (item.annotation?.id === hoveredAnnotation?.id || item.annotation?.id === clickedAnnotation?.id)
    ) {
      return 'tw-bg-cornflower-200'
    }

    if (item.draft) {
      return 'tw-border-neutral-300 tw-bg-neutral-100 tw-rounded-lg tw-border-dashed tw-border-2'
    } else if (isSubmittedByCustomer) {
      return 'tw-bg-cornflower-50'
    } else if (item.private) {
      return 'tw-bg-sunnyyellow-50'
    } else if (item.taskType === AdminTaskType.Conversation) {
      return 'tw-bg-picklegreen-100'
    }
    return 'tw-bg-neutral-100'
  }

  function handleCancelClick() {
    setIsEditMode(false)
    setEditedBodyValue(item.body)
  }

  async function handleSaveClick() {
    try {
      await updateTimelineItem(item, editedBodyValue)
      setIsEditMode(false)
      toast.success(`${capitalize(displayItemType)} updated`)
    } catch (error) {
      console.error(`Error updating ${displayItemType}`, error)
      toast.error(`Error updating ${displayItemType}`)
    }
  }

  const previewUrl = useS3PreviewImage(item.annotation)

  const thumbnailSource = getPreviewUrl(previewUrl)

  function handleMouseEnter(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault()
    if (isAnnotation(item)) {
      setHoveredAnnotation(item.annotation)
    }
  }

  function handleMouseLeave(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault()
    if (isAnnotation(item)) {
      setHoveredAnnotation(null)
    }
  }

  function handleClickedItem() {
    if (isAnnotation(item)) {
      setClickedAnnotation(item.annotation, item.ticketVersion)
    }
  }

  async function handleCopyLinkToThread() {
    await navigator.clipboard.writeText(window.location.origin + window.location.pathname + `#${containerId}`)
    toast.success('Successfully copied link')
  }

  useEffect(() => {
    let delayedScrollTimeout
    if (window.location.hash?.slice(1) === containerId) {
      delayedScrollTimeout = setTimeout(() => {
        document.getElementById(containerId).scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }

    return () => {
      clearTimeout(delayedScrollTimeout)
    }
  }, [containerId])

  useEffect(() => {
    async function parseBody() {
      if (isFeatureFlagEnabled('request_wysiwyg') && item.taskType !== AdminTaskType.Annotation) {
        const directionParsed = parseDirectionHTML(item.body)
        const withAssetUrls = await parseAssetUrls(directionParsed)
        setParsedBody(withAssetUrls)
      } else {
        setParsedBody(isSubmittedByCustomer ? hyperlink(item.body) : item.body)
      }
    }
    parseBody()
  }, [item.body, item.taskType, isFeatureFlagEnabled, isSubmittedByCustomer])

  return (
    <li className={`${classNames.li} ${isSubmittedByCustomer ? 'tw-justify-start' : 'tw-justify-end'}`}>
      {item.taskType !== AdminTaskType.Conversation && (
        <Checkbox onClick={handleCheckboxClick} isChecked={item.completed} disabled={checkDisabled} />
      )}
      <article
        className={`${classNames.article} ${itemColorClass()}`}
        onClick={handleClickedItem}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        id={containerId}
        data-testid="timeline-item"
      >
        <header className={classNames.header.container}>
          {item?.user?.avatar && (
            <div className={classNames.header.avatar}>
              <Avatar src={item.user.avatar} name={item.user.fullName} />
            </div>
          )}
          <div className={classNames.header.byline}>
            <div>{item.user?.fullName || ''}</div>
            <div className="tw-pl-2 tw-text-neutral-600">{displayDate(item.createdAt)}</div>
          </div>
          <IconFlyoutMenu color="transparent" icon={['far', 'ellipsis-v']}>
            <IconFlyoutMenu.Button onClick={handleCopyLinkToThread}>
              <div className="tw-flex tw-items-center tw-gap-2">
                <LinkIcon className="lu-light lu-md" />
                Copy link to thread
              </div>
            </IconFlyoutMenu.Button>
            {item.meta?.permissions?.update && canEdit && (
              <IconFlyoutMenu.Button onClick={() => setIsEditMode(true)}>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <PencilIcon className="lu-light lu-md" />
                  Edit
                </div>
              </IconFlyoutMenu.Button>
            )}
          </IconFlyoutMenu>
        </header>
        <div className={classNames.body}>
          <div className="tw-mb-2 tw-flex tw-items-center">
            <TypeTitle type={item.taskType} />
            {item.taskType === AdminTaskType.Annotation && (
              <div className="tw-flex tw-items-center tw-gap-2" rel="noreferrer" data-testid="annotation-link">
                <img src={thumbnailSource} className="tw-h-5 tw-w-5" alt="thumbnail" />
                <div className="tw-break-all tw-font-bold tw-text-neutral-800">{fileName}</div>
                <div className="tw-text-neutral-600" data-testid="annotation-file-caption">
                  {fileCaption}
                </div>
              </div>
            )}
          </div>
          {isEditMode ? (
            <>
              <WYSIWYGTextarea
                showCannedResponseButton
                allowFileDrop
                defaultValue={item.body}
                onChange={(v) => setEditedBodyValue(v)}
              />
              <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-2 tw-pt-4">
                <IconButton color="secondary" onClick={handleCancelClick} dataTestid="cancel-update">
                  <XIcon className="lu-light lu-sm" />
                </IconButton>
                <IconButton color="primary" onClick={handleSaveClick} dataTestid="submit-update">
                  <SendIcon className="lu-light lu-sm" />
                </IconButton>
              </div>
            </>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: parsedBody,
              }}
            />
          )}
        </div>
      </article>
    </li>
  )
}
